import * as am5 from '@amcharts/amcharts5';
import { Root } from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import {
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  ValueAxis,
  XYChart,
  XYChartScrollbar,
} from '@amcharts/amcharts5/xy';
import { useCallback, useEffect, useState } from 'react';
import { ChartOption } from '../../../interface/reports';
import {
  ExecutionsOverTime,
  ExecutionsOverTimeData,
  ExecutionsOverTimeDateData,
  ExecutionsOverTimeProcessorData,
} from '../../../resources/executions-over-time/executions-over-time-model';
import { Item } from '../../ui/dropdown-btn';

type Props = {
  id: string;
  data: ExecutionsOverTime;
  activeChart: Item | undefined | null;
};

export default function ExecutionsOverTimeStackedChartComponent({ id, data, activeChart }: Props) {
  const [root, setRoot] = useState<Root>();
  const [chart, setChart] = useState<XYChart>();

  const transformExecutionsOverTimeData = useCallback(
    (_rawData: ExecutionsOverTime) => {
      let transformedData: ExecutionsOverTimeDateData[] = [];
      let seriesNames: Set<string> = new Set();

      data.dateData.forEach((dateEntry: ExecutionsOverTimeDateData) => {
        dateEntry.countsByName.forEach((nameEntry: ExecutionsOverTimeProcessorData) => {
          seriesNames.add(nameEntry.processorName);
        });

        transformedData.push(dateEntry);
      });
      transformedData = transformedData.sort((a, b) => a.date.localeCompare(b.date));

      return { transformedData, seriesNames: Array.from(seriesNames) };
    },
    [data.dateData],
  );

  useEffect(() => {
    const newRoot = Root.new(id);
    setRoot(newRoot);

    // Cleanup function to dispose root on unmount
    return () => {
      newRoot.dispose();
    };
  }, [id]);

  useEffect(() => {
    if (root && !chart) {
      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: true,
          wheelX: 'panX',
          wheelY: 'zoomX',
          layout: root.verticalLayout,
        }),
      );

      // We don't want zoom-out button to appear while animating, so we hide it
      chart.zoomOutButton.set('forceHidden', true);

      setChart(chart);
    }
  }, [root, chart]);

  useEffect(() => {
    if (root && chart) {
      // Remove any old axis or series data, if the chart has been rendered already
      chart.xAxes.clear();
      chart.yAxes.clear();
      chart.series.clear();

      let xAxis = chart.xAxes.push(
        CategoryAxis.new(root, {
          categoryField: 'date',
          renderer: AxisRendererX.new(root, {
            minGridDistance: 100,
          }),
        }),
      );

      xAxis.get('renderer').grid.template.setAll({
        location: 0.5,
        strokeOpacity: 0.1,
      });

      xAxis.get('renderer').labels.template.setAll({
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
        paddingTop: 10,
      });

      let yAxis = chart.yAxes.push(
        ValueAxis.new(root, {
          renderer: AxisRendererY.new(root, {}),
          calculateTotals: true,
        }),
      );

      const scrollbarX = XYChartScrollbar.new(root, {
        orientation: 'horizontal',
        marginBottom: 50,
      });

      // Transform JSON map structure to format that can be accepted by AmCharts
      const { transformedData, seriesNames } = transformExecutionsOverTimeData(data);
      xAxis.data.setAll(transformedData);

      seriesNames.forEach(name => {
        let series = chart.series.push(
          ColumnSeries.new(root, {
            name: name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: name,
            categoryXField: 'date',
            stacked: false,
            clustered: false,
          }),
        );

        chart.set('scrollbarX', scrollbarX);

        // Adds processor name, date of executions, and execution status data described below to the tooltips
        series.columns.template.setAll({
          tooltipText: '{name}: [bold]{valueY}[/]\nDate: {date}\n\n{additionalInfo}',
          width: am5.percent(40),
          strokeOpacity: 0,
        });

        // Add tooltip data
        series.columns.template.adapters.add('tooltipText', (text, target) => {
          const dataItem = target.dataItem;
          if (dataItem) {
            const dataContext = dataItem.dataContext as { [key: string]: any };
            let additionalInfo = '';

            // Append processor status counts to tooltip info
            const statusCounts: ExecutionsOverTimeData[] = dataContext['statusCounts'];

            if (statusCounts) {
              statusCounts.forEach((statusCount, index) => {
                additionalInfo += `${statusCount.status}: [bold]${statusCount.numberOfExecutions}[/]`;
                additionalInfo += index === statusCounts.length - 1 ? '' : '\n';
              });
            }

            return text?.replace('{additionalInfo}', additionalInfo.trim());
          }
          return text;
        });

        // Set the column data for each date in the series
        series.data.setAll(
          transformedData.map(item => {
            const processorCounts = item.countsByName.find(item => item.processorName === name);
            const sumExecutions = processorCounts ? processorCounts.totalExecutions : 0;

            // Only include processors where the count for that day was > 0
            if (sumExecutions > 0) {
              // This is contextual data for the data item, and needs to be modified when adding tooltip info
              return {
                date: item.date,
                [name]: sumExecutions,
                name: name,
                totalExecutions: sumExecutions,
                statusCounts: processorCounts ? processorCounts.statusCounts : [],
              };
            }
            return {};
          }),
        );
      });
    }
  }, [chart, data, root, transformExecutionsOverTimeData]);

  return (
    <div
      hidden={activeChart?.id !== ChartOption[ChartOption.EXECUTIONS_OVER_TIME]}
      id={id}
      style={{ height: '500px', marginTop: '50px' }}
    />
  );
}
